// Copyright 2026 FlairCV Inc.
// SPDX-License-Identifier: LicenseRef-FlairCV-Enterprise

import { FileText, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { PasswordInput } from '@/app/components/password-input';
import { useRegister, registerSchema, OAuthButtons, RecaptchaGuard, useCaptchaToken } from '@/app/features/auth';
import type { RegisterFormData } from '@/app/features/auth';

function RegisterForm() {
  const [formData, setFormData] = useState<RegisterFormData>({
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<keyof RegisterFormData, string>>
  >({});
  const register = useRegister();
  const getCaptchaToken = useCaptchaToken();

  function handleChange(field: keyof RegisterFormData, value: string) {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFieldErrors({});

    const result = registerSchema.safeParse(formData);
    if (!result.success) {
      const errors: Partial<Record<keyof RegisterFormData, string>> = {};
      for (const issue of result.error.issues) {
        const field = issue.path[0] as keyof RegisterFormData;
        if (!errors[field]) {
          errors[field] = issue.message;
        }
      }
      setFieldErrors(errors);
      return;
    }

    const captchaToken = await getCaptchaToken('register');
    register.mutate({ email: result.data.email, password: result.data.password, captchaToken });
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-[400px]">
        {/* Logo */}
        <a href="/" className="mb-8 flex items-center justify-center gap-2">
          <FileText className="h-8 w-8 text-primary" />
          <span className="text-[24px] font-semibold tracking-tight">FlairCV</span>
        </a>

        {/* Register form */}
        <div className="rounded-lg border border-border bg-card p-6 shadow-sm">
          <h1 className="text-[18px] font-semibold text-card-foreground">Create account</h1>
          <p className="mt-1 text-[13px] text-muted-foreground">
            Start building your career database. Get $1.00 in free credits.
          </p>

          {/* Server error */}
          {register.error && (
            <div
              className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-[13px] text-destructive"
              role="alert"
            >
              {register.error.message}
            </div>
          )}

          <form className="mt-6 space-y-4" onSubmit={handleSubmit} noValidate>
            <div>
              <label
                htmlFor="email"
                className="mb-1.5 block text-[13px] font-medium text-foreground"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className="block w-full rounded-md border border-input bg-background px-3 py-2 text-[14px] text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                placeholder="you@example.com"
                required
                autoComplete="email"
                aria-invalid={!!fieldErrors.email}
                aria-describedby={fieldErrors.email ? 'email-error' : undefined}
              />
              {fieldErrors.email && (
                <p id="email-error" className="mt-1 text-[12px] text-destructive">
                  {fieldErrors.email}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="password"
                className="mb-1.5 block text-[13px] font-medium text-foreground"
              >
                Password
              </label>
              <PasswordInput
                id="password"
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                className="block w-full rounded-md border border-input bg-background px-3 py-2 text-[14px] text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                placeholder="Minimum 8 characters"
                required
                autoComplete="new-password"
                aria-invalid={!!fieldErrors.password}
                aria-describedby={fieldErrors.password ? 'password-error' : undefined}
              />
              {fieldErrors.password && (
                <p id="password-error" className="mt-1 text-[12px] text-destructive">
                  {fieldErrors.password}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="confirm-password"
                className="mb-1.5 block text-[13px] font-medium text-foreground"
              >
                Confirm password
              </label>
              <PasswordInput
                id="confirm-password"
                reveals="confirmed password"
                value={formData.confirmPassword}
                onChange={(e) => handleChange('confirmPassword', e.target.value)}
                className="block w-full rounded-md border border-input bg-background px-3 py-2 text-[14px] text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                placeholder="Repeat your password"
                required
                autoComplete="new-password"
                aria-invalid={!!fieldErrors.confirmPassword}
                aria-describedby={
                  fieldErrors.confirmPassword ? 'confirm-password-error' : undefined
                }
              />
              {fieldErrors.confirmPassword && (
                <p id="confirm-password-error" className="mt-1 text-[12px] text-destructive">
                  {fieldErrors.confirmPassword}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={register.isPending}
              className="flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-[14px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
            >
              {register.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Create account
            </button>
          </form>

          <div className="relative mt-6 mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-[12px]">
              <span className="bg-card px-2 text-muted-foreground">or</span>
            </div>
          </div>

          <OAuthButtons />

          <div className="mt-4 text-center text-[13px] text-muted-foreground">
            Already have an account?{' '}
            <Link
              to="/auth/login"
              className="font-medium text-primary hover:underline"
            >
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export function RegisterPage() {
  return (
    <RecaptchaGuard>
      <RegisterForm />
    </RecaptchaGuard>
  );
}
